import type { ProductShape } from '@/previewThemes/catalog/types';

/**
 * The pictures in a theme preview.
 *
 * Drawn, not fetched. A preview has to look like a populated shop on a laptop
 * with no network, on a first paint, in a screenshot test and behind whatever
 * corporate proxy the merchant is sitting behind - and it has to do that
 * without the repository carrying a hundred product photographs it does not
 * have the rights to. So every picture here is an SVG generated from the
 * product's own slug: deterministic, a couple of hundred bytes, and inlined as
 * a data URI so it cannot 404.
 *
 * They are abstract on purpose. A merchant looking at a preview is deciding
 * whether they like the *theme*, and a preview full of convincing stock
 * photography quietly moves that judgement onto the photographs - which are
 * the one thing they will not be getting.
 */

/**
 * A stable number from a string.
 *
 * FNV-1a: short, has no collisions worth caring about at this scale, and -
 * unlike anything involving `Math.random` - gives the same product the same
 * picture on every render, which is what stops a preview shuffling itself
 * every time React re-mounts a grid.
 */
export function hash(seed: string): number {
  let value = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    value ^= seed.charCodeAt(index);
    value = Math.imul(value, 0x01000193);
  }
  return value >>> 0;
}

/**
 * The palettes a generated picture is painted in.
 *
 * Muted and desaturated throughout: these sit behind a *theme's* colours in a
 * grid of twelve, and a wall of saturated tiles would drown whatever palette
 * the merchant is actually there to look at.
 */
const PALETTES: [string, string][] = [
  ['#eceae4', '#8d8676'],
  ['#e6e9ec', '#6f7d8a'],
  ['#efe7e3', '#a1817a'],
  ['#e5ebe6', '#71887a'],
  ['#eeeaef', '#867a92'],
  ['#f0ece2', '#9a8757'],
  ['#e8e8ea', '#7c7c86'],
  ['#f1e8e6', '#a37f75'],
  ['#e4e9ee', '#63788c'],
  ['#efeee7', '#8f8b6d'],
];

function paletteFor(seed: string, override?: [string, string]): [string, string] {
  return override ?? PALETTES[hash(seed) % PALETTES.length];
}

/**
 * The silhouette a product is drawn as, centred in an 800x800 box.
 *
 * One shape per broad kind of thing a shop sells. Enough that a grid reads as
 * a catalogue of different objects rather than a grid of the same rectangle,
 * and few enough that they can all be drawn by hand and look deliberate.
 */
const SHAPES: Record<ProductShape, (fill: string) => string> = {
  garment: (f) =>
    `<path fill="${f}" d="M300 250l-90 45 35 85 45-22v235a20 20 0 0020 20h180a20 20 0 0020-20V358l45 22 35-85-90-45-50-18a60 60 0 01-100 0z"/>`,
  bottle: (f) =>
    `<path fill="${f}" d="M368 210h64v70c0 26 12 38 28 58 18 22 26 44 26 76v186a30 30 0 01-30 30h-112a30 30 0 01-30-30V414c0-32 8-54 26-76 16-20 28-32 28-58z"/>`,
  box: (f) =>
    `<path fill="${f}" d="M400 210l190 96v192l-190 96-190-96V306z"/><path fill="#ffffff" opacity=".22" d="M400 210l190 96-190 96-190-96z"/>`,
  device: (f) =>
    `<rect x="272" y="188" width="256" height="424" rx="34" fill="${f}"/><rect x="302" y="238" width="196" height="304" rx="8" fill="#ffffff" opacity=".28"/>`,
  chair: (f) =>
    `<path fill="${f}" d="M258 200h284v58H258z"/><path fill="${f}" d="M282 258h236l-24 176H306z"/><path fill="${f}" d="M292 452h216v34H292z"/><path fill="${f}" d="M312 486h26v134h-26zM462 486h26v134h-26z"/>`,
  jar: (f) =>
    `<rect x="316" y="204" width="168" height="52" rx="16" fill="${f}"/><path fill="${f}" d="M298 268h204a22 22 0 0122 22v268a42 42 0 01-42 42H318a42 42 0 01-42-42V290a22 22 0 0122-22z"/>`,
  bag: (f) =>
    `<path fill="${f}" d="M264 300h272l30 300H234z"/><path fill="none" stroke="${f}" stroke-width="22" d="M336 316v-42a64 64 0 01128 0v42"/>`,
  ring: (f) =>
    `<circle cx="400" cy="440" r="150" fill="none" stroke="${f}" stroke-width="44"/><path fill="${f}" d="M400 196l58 62-58 58-58-58z"/>`,
  book: (f) =>
    `<path fill="${f}" d="M240 214h150c26 0 46 18 46 42v344c0-18-16-32-40-32H240z"/><path fill="${f}" opacity=".72" d="M560 214H410c-26 0-46 18-46 42v344c0-18 16-32 40-32h156z"/>`,
  plant: (f) =>
    `<path fill="${f}" d="M330 470h140l-20 156H350z"/><path fill="${f}" d="M392 460c-70-10-118-62-118-136 74 0 122 48 126 122 8-72 56-118 128-118 0 76-52 128-128 134z"/>`,
  // A cordless drill, drawn as its three masses: motor, chuck, battery. The
  // three below are the hardware trade's, and a shop that sells drills reads
  // wrong drawn as a grid of jars.
  tool: (f) =>
    `<rect x="196" y="292" width="330" height="150" rx="42" fill="${f}"/><path fill="${f}" d="M526 336h56v-24l64 22v76l-64 22v-24h-56z"/><path fill="${f}" d="M296 442h120l-26 116h-116z"/><rect x="246" y="552" width="176" height="76" rx="18" fill="${f}"/>`,
  wrench: (f) =>
    `<path fill="${f}" d="M330 190h52v56h36v-56h52v106a32 32 0 01-32 32h-76a32 32 0 01-32-32z"/><rect x="366" y="316" width="68" height="212" rx="16" fill="${f}"/><circle cx="400" cy="580" r="60" fill="none" stroke="${f}" stroke-width="38"/>`,
  helmet: (f) =>
    `<path fill="${f}" d="M400 236c-84 0-152 66-152 148v66h304v-66c0-82-68-148-152-148z"/><rect x="186" y="450" width="428" height="56" rx="28" fill="${f}"/><path fill="#ffffff" opacity=".25" d="M368 240h64v210h-64z"/>`,
};

/** The shape a product gets when its catalog entry does not name one. */
function shapeFor(seed: string): ProductShape {
  const names = Object.keys(SHAPES) as ProductShape[];
  return names[hash(`shape:${seed}`) % names.length];
}

/** An SVG document as a data URI an `<img src>` can take. */
function dataUri(svg: string): string {
  // Encoded rather than base64: it stays readable in devtools and is shorter
  // for markup this repetitive.
  return `data:image/svg+xml,${encodeURIComponent(svg.replace(/\s+/g, ' ').trim())}`;
}

/**
 * A product's picture: a tinted ground, a soft light, and the object on it.
 *
 * Square, because every tile in the storefront reserves a square box for it
 * and a picture of another shape would letterbox inside that box.
 */
export function productArtwork(
  seed: string,
  options: { shape?: ProductShape; palette?: [string, string] } = {},
): string {
  const [light, deep] = paletteFor(seed, options.palette);
  const shape = options.shape ?? shapeFor(seed);
  const drift = hash(`drift:${seed}`) % 120;

  return dataUri(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 800" width="800" height="800">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" stop-color="${light}"/>
          <stop offset="1" stop-color="${deep}" stop-opacity="0.34"/>
        </linearGradient>
        <radialGradient id="l" cx="0.32" cy="0.24" r="0.8">
          <stop offset="0" stop-color="#ffffff" stop-opacity="0.75"/>
          <stop offset="1" stop-color="#ffffff" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <rect width="800" height="800" fill="url(#g)"/>
      <circle cx="${180 + drift}" cy="${640 - drift}" r="300" fill="${deep}" opacity="0.10"/>
      <rect width="800" height="800" fill="url(#l)"/>
      ${SHAPES[shape](deep)}
      <ellipse cx="400" cy="668" rx="168" ry="26" fill="${deep}" opacity="0.16"/>
    </svg>
  `);
}

/**
 * A shelf's picture: no object, just the shop's own colour and a hint of
 * structure. A department is not a thing, so drawing one as a thing reads
 * wrong on every tile it appears on.
 */
export function categoryArtwork(seed: string, palette?: [string, string]): string {
  const [light, deep] = paletteFor(`category:${seed}`, palette);
  const angle = hash(`angle:${seed}`) % 60;

  return dataUri(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 800" width="1200" height="800">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="${light}"/>
          <stop offset="1" stop-color="${deep}" stop-opacity="0.55"/>
        </linearGradient>
      </defs>
      <rect width="1200" height="800" fill="url(#g)"/>
      <g opacity="0.16" fill="${deep}" transform="rotate(${angle - 30} 600 400)">
        <circle cx="380" cy="400" r="250"/>
        <rect x="620" y="150" width="500" height="500" rx="60"/>
      </g>
      <g opacity="0.30" stroke="${deep}" stroke-width="2" fill="none">
        <path d="M0 620h1200M0 180h1200"/>
      </g>
    </svg>
  `);
}

/**
 * A hero or banner picture: the same language, wider and quieter, because
 * headline copy is set on top of it.
 */
export function bannerArtwork(seed: string, palette?: [string, string]): string {
  const [light, deep] = paletteFor(`banner:${seed}`, palette);

  return dataUri(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 900" width="1600" height="900">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="0.7">
          <stop offset="0" stop-color="${deep}" stop-opacity="0.62"/>
          <stop offset="1" stop-color="${light}"/>
        </linearGradient>
      </defs>
      <rect width="1600" height="900" fill="url(#g)"/>
      <circle cx="1180" cy="300" r="420" fill="${light}" opacity="0.42"/>
      <circle cx="360" cy="760" r="300" fill="${deep}" opacity="0.14"/>
    </svg>
  `);
}

/** A post's illustration - the same language, reduced to a bar chart of tone. */
export function postArtwork(seed: string): string {
  const [light, deep] = paletteFor(`post:${seed}`);
  const bars = [0, 1, 2, 3].map((index) => {
    const x = 120 + index * 320;
    const height = 180 + (hash(`${seed}:${index}`) % 340);
    return `<rect x="${x}" y="${760 - height}" width="220" height="${height}" rx="16" fill="${deep}" opacity="${0.12 + index * 0.06}"/>`;
  });

  return dataUri(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 900" width="1600" height="900">
      <rect width="1600" height="900" fill="${light}"/>
      ${bars.join('')}
    </svg>
  `);
}
